/**
 * calcPatterns.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pattern detection functions for MarketPulze charts.
 *
 * Exports:
 *   detectCandlePatterns(chartData)               — 8 candlestick pattern types
 *   detectSignalBadges(chartData, activeInds, registry) — indicator-based signals
 *   detectCombinationSignals(chartData, activeInds, registry) — multi-condition moat
 *
 * Design principles:
 *   1. Pure functions — no React, no D3, no DOM side effects
 *   2. Input: chartData = display format [{date, open, high, low, close, volume}]
 *   3. Output: array of signal objects ready for the renderer to paint on canvas
 *   4. Each function is independently testable with mock OHLC arrays
 *   5. indicatorRegistry is passed as parameter (not imported) to avoid
 *      circular deps and make unit testing trivial
 *
 * Signal object shape:
 *   {
 *     index:     number   — chartData array index where signal fires
 *     label:     string   — short display text
 *     color:     string   — hex/rgba colour for the badge
 *     direction: "bull" | "bear" | "neutral"
 *     desc:      string   — plain-English explanation shown in tooltip
 *     type?:     string   — machine-readable key for deduplication
 *   }
 *
 * FIX (v8): detectCandlePatterns dedup was broken — the .filter() with a
 *   mutable Map could emit both a neutral Doji AND a directional Engulfing
 *   for the same candle index. Fixed with a single-pass Map that overwrites
 *   neutral entries when a directional signal is found for the same candle.
 *
 * FIX (v8): removed redundant `if (i >= 2)` guard inside a loop starting at
 *   i = 2 — the condition was always true.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Candlestick Pattern Detection ─────────────────────────────────────────────

/**
 * detectCandlePatterns(chartData)
 *
 * Scans chartData for 8 classic Japanese candlestick patterns.
 * Returns at most one signal per candle index (strongest pattern wins).
 *
 * Patterns detected:
 *   Doji         — body < 10% of range → indecision (neutral)
 *   Hammer       — long lower wick, small body at top after downtrend → bullish
 *   Shooting Star — long upper wick, small body after uptrend → bearish
 *   Bullish Engulfing — bull candle completely engulfs prior bear → bullish
 *   Bearish Engulfing — bear candle completely engulfs prior bull → bearish
 *   Morning Star  — bear + small middle + bull above bear midpoint → bullish 3-bar
 *   Evening Star  — bull + small middle + bear below bull midpoint → bearish 3-bar
 *   Marubozu      — body > 92% of range (no wicks) → strong conviction
 *
 * @param {Array} chartData — [{date, open, high, low, close, volume}, ...]
 * @returns {Array} signal objects, one per candle index, sorted by index
 */
export function detectCandlePatterns(chartData) {
  // byIndex: single source of truth — one winner per candle index.
  // Directional signals (bull/bear) always beat neutral (Doji).
  // If two directional signals hit the same candle, first one wins.
  const byIndex = new Map();

  const record = (sig) => {
    const existing = byIndex.get(sig.index);
    if (!existing) {
      byIndex.set(sig.index, sig);
      return;
    }
    // Upgrade neutral → directional; never downgrade directional
    if (existing.direction === 'neutral' && sig.direction !== 'neutral') {
      byIndex.set(sig.index, sig);
    }
  };

  const d = chartData;

  // Loop starts at 2 so 3-bar lookback (i-2) is always safe
  for (let i = 2; i < d.length; i++) {
    const c = d[i], p = d[i - 1];

    // ── Candle geometry ────────────────────────────────────────────────────
    const cBody      = Math.abs(c.close - c.open);
    const cRange     = c.high - c.low || 0.0001;
    const pBody      = Math.abs(p.close - p.open);
    const pRange     = p.high - p.low || 0.0001;
    const cBull      = c.close >= c.open;
    const pBull      = p.close >= p.open;
    const cLowerWick = cBull ? c.open  - c.low  : c.close - c.low;
    const cUpperWick = cBull ? c.high  - c.close : c.high  - c.open;

    // ── Doji: body < 10% of total range ───────────────────────────────────
    if (cBody / cRange < 0.1) {
      record({
        index: i, label: 'Doji', color: '#94a3b8', direction: 'neutral',
        desc: 'Indecision — buyers and sellers equally matched',
        type: 'doji',
      });
    }

    // ── Hammer: long lower wick after downtrend ────────────────────────────
    if (!pBull && cLowerWick > cBody * 2 && cUpperWick < cBody * 0.5 && cBody / cRange > 0.1) {
      record({
        index: i, label: 'Hammer', color: '#10b981', direction: 'bull',
        desc: 'Bullish reversal — lower wick shows rejected selling pressure',
        type: 'hammer',
      });
    }

    // ── Shooting Star: long upper wick after uptrend ───────────────────────
    if (pBull && cUpperWick > cBody * 2 && cLowerWick < cBody * 0.5 && cBody / cRange > 0.1) {
      record({
        index: i, label: 'Star', color: '#ef4444', direction: 'bear',
        desc: 'Bearish reversal — upper wick shows rejected buying pressure',
        type: 'shooting_star',
      });
    }

    // ── Bullish Engulfing ──────────────────────────────────────────────────
    if (!pBull && cBull && c.open < p.close && c.close > p.open && cBody > pBody) {
      record({
        index: i, label: 'Eng↑', color: '#10b981', direction: 'bull',
        desc: 'Bullish engulfing — strong buying overwhelmed prior selling',
        type: 'bull_engulf',
      });
    }

    // ── Bearish Engulfing ──────────────────────────────────────────────────
    if (pBull && !cBull && c.open > p.close && c.close < p.open && cBody > pBody) {
      record({
        index: i, label: 'Eng↓', color: '#ef4444', direction: 'bear',
        desc: 'Bearish engulfing — strong selling overwhelmed prior buying',
        type: 'bear_engulf',
      });
    }

    // ── Morning Star / Evening Star (3-bar) ────────────────────────────────
    // Guard `i >= 2` removed — loop already starts at i=2, guard was always true
    const pp2     = d[i - 2];
    const pp2Bull = pp2.close >= pp2.open;
    const pp2Mid  = (pp2.open + pp2.close) / 2;

    // Morning Star: bear → small middle → bull closing above bear's midpoint
    if (!pp2Bull && pBody / pRange < 0.35 && cBull && c.close > pp2Mid) {
      record({
        index: i, label: 'M⭐', color: '#10b981', direction: 'bull',
        desc: 'Morning star — 3-bar bullish reversal pattern',
        type: 'morning_star',
      });
    }

    // Evening Star: bull → small middle → bear closing below bull's midpoint
    if (pp2Bull && pBody / pRange < 0.35 && !cBull && c.close < pp2Mid) {
      record({
        index: i, label: 'E⭐', color: '#ef4444', direction: 'bear',
        desc: 'Evening star — 3-bar bearish reversal pattern',
        type: 'evening_star',
      });
    }

    // ── Marubozu: full-body candle, virtually no wicks ─────────────────────
    if (cBody / cRange > 0.92) {
      record({
        index: i,
        label:     cBull ? 'MBull' : 'MBear',
        color:     cBull ? '#10b981' : '#ef4444',
        direction: cBull ? 'bull' : 'bear',
        desc: cBull
          ? 'Bullish Marubozu — strong conviction buying, no sellers'
          : 'Bearish Marubozu — strong conviction selling, no buyers',
        type: cBull ? 'marubozu_bull' : 'marubozu_bear',
      });
    }
  }

  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

// ── Indicator Signal Badge Detection ──────────────────────────────────────────

/**
 * detectSignalBadges(chartData, activeInds, indicatorRegistry)
 *
 * Scans active indicator results for crossover events and returns badge
 * objects for rendering on the chart canvas at the exact candle.
 *
 * Signals detected:
 *   RSI crosses above 70  → "OB" red badge
 *   RSI crosses below 30  → "OS" green badge
 *   MACD histogram flips +→ "M↑" green badge
 *   MACD histogram flips -→ "M↓" red badge
 *   Close breaks above upper BB → "BB↑" amber badge
 *   Close breaks below lower BB → "BB↓" purple badge
 *
 * @param {Array}  chartData         — [{date, open, high, low, close, volume}]
 * @param {Array}  activeInds        — [{key, params, instanceId, ...}]
 * @param {Object} indicatorRegistry — INDICATOR_REGISTRY from chartConfig.js
 * @returns {Array} signal badge objects
 */
export function detectSignalBadges(chartData, activeInds, indicatorRegistry) {
  const signals = [];
  if (!chartData.length || !activeInds.length || !indicatorRegistry) return signals;

  // Convert display format → raw format for indicator calc functions
  const raw = chartData.map(d => ({
    t: d.date.getTime() / 1000,
    o: d.open, h: d.high, l: d.low, c: d.close, v: d.volume,
  }));

  // O(1) timestamp → chartData index lookup
  const tsToIdx = new Map(chartData.map((d, i) => [d.date.getTime(), i]));
  const getIdx  = ts => { const i = tsToIdx.get(ts * 1000); return i !== undefined ? i : -1; };

  activeInds.forEach(ind => {
    const entry = indicatorRegistry[ind.key];
    if (!entry) return;
    const result = entry.calc(raw, ind.params, false, '#fff');
    if (!result) return;

    // ── RSI overbought / oversold ──────────────────────────────────────────
    if (ind.key === 'rsi' && result.lines?.[0]?.data?.length >= 2) {
      const rsiData = result.lines[0].data;
      rsiData.forEach((pt, i) => {
        if (i === 0) return;
        const prev = rsiData[i - 1].value, cur = pt.value;
        const idx  = getIdx(pt.time);
        if (idx < 0) return;
        if (prev < 70 && cur >= 70)
          signals.push({ index: idx, type: 'rsi_ob', label: 'OB', color: '#ef4444', direction: 'bear',
            desc: `RSI crossed above 70 (${cur.toFixed(1)}) — overbought warning` });
        else if (prev > 30 && cur <= 30)
          signals.push({ index: idx, type: 'rsi_os', label: 'OS', color: '#10b981', direction: 'bull',
            desc: `RSI crossed below 30 (${cur.toFixed(1)}) — oversold opportunity` });
      });
    }

    // ── MACD histogram sign flip ───────────────────────────────────────────
    if (ind.key === 'macd' && result.macd?.histogram?.length >= 2) {
      const hist = result.macd.histogram;
      hist.forEach((pt, i) => {
        if (i === 0) return;
        const prev = hist[i - 1].value, cur = pt.value;
        const idx  = getIdx(pt.time);
        if (idx < 0) return;
        if (prev <= 0 && cur > 0)
          signals.push({ index: idx, type: 'macd_bull', label: 'M↑', color: '#10b981', direction: 'bull',
            desc: 'MACD histogram flipped positive — bullish momentum shift' });
        else if (prev >= 0 && cur < 0)
          signals.push({ index: idx, type: 'macd_bear', label: 'M↓', color: '#ef4444', direction: 'bear',
            desc: 'MACD histogram flipped negative — bearish momentum shift' });
      });
    }

    // ── Bollinger Band breach ──────────────────────────────────────────────
    // BB lines: [0]=upper, [1]=middle, [2]=lower
    if (ind.key === 'bb' && result.lines?.length >= 3) {
      const upper = result.lines[0].data, lower = result.lines[2].data;
      upper.forEach((pt, i) => {
        const loPt = lower[i]; if (!loPt) return;
        const idx  = getIdx(pt.time);
        if (idx < 0) return;
        const candle = chartData[idx];
        if (candle.close > pt.value)
          signals.push({ index: idx, type: 'bb_breach_up', label: 'BB↑', color: '#f59e0b', direction: 'bear',
            desc: 'Close above upper Bollinger Band — extended, watch for reversal' });
        else if (candle.close < loPt.value)
          signals.push({ index: idx, type: 'bb_breach_dn', label: 'BB↓', color: '#a78bfa', direction: 'bull',
            desc: 'Close below lower Bollinger Band — oversold, watch for bounce' });
      });
    }
  });

  // Deduplicate: one badge per candle per signal type
  const seen = new Set();
  return signals.filter(s => {
    const key = `${s.index}-${s.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Combination Signal Detection (the product moat) ───────────────────────────

/**
 * detectCombinationSignals(chartData, activeInds, indicatorRegistry)
 *
 * Fires when multiple conditions are simultaneously true on the same candle.
 * This is the D3 Tier 3 moat — no competitor renders these on the canvas.
 *
 * Each result includes a `conditions` array for the tooltip breakdown.
 *
 * Currently implemented:
 *   BULLISH_REVERSAL  — Hammer + RSI(14) < 35 + close within 1% of lower BB
 *   BEARISH_REVERSAL  — Shooting Star + RSI(14) > 65 + close within 1% of upper BB
 *   MACD_PATTERN_BULL — Morning Star + MACD histogram flipped positive same/next bar
 *   MACD_PATTERN_BEAR — Evening Star + MACD histogram flipped negative same/next bar
 *   VOLUME_SURGE_BULL — Hammer/Engulfing + Volume > 2× 20-bar avg + RSI < 40
 *
 * Implementation note: pass signalBadges (from detectSignalBadges) and
 * patterns (from detectCandlePatterns) as inputs to avoid re-calculating
 * indicators twice.
 *
 * @param {Array}  chartData
 * @param {Array}  signalBadges      — output of detectSignalBadges()
 * @param {Array}  candlePatterns    — output of detectCandlePatterns()
 * @returns {Array} combination signal objects with full conditions breakdown
 */
export function detectCombinationSignals(chartData, signalBadges, candlePatterns) {
  if (!chartData.length || !signalBadges.length || !candlePatterns.length) return [];

  const results = [];

  // Build fast lookup sets
  const badgeByIndex = new Map();
  signalBadges.forEach(b => {
    if (!badgeByIndex.has(b.index)) badgeByIndex.set(b.index, []);
    badgeByIndex.get(b.index).push(b.type);
  });

  const patternByIndex = new Map();
  candlePatterns.forEach(p => patternByIndex.set(p.index, p.type));

  const hasPattern = (idx, type) => patternByIndex.get(idx) === type; // eslint-disable-line no-unused-vars
  const hasBadge   = (idx, type) => badgeByIndex.get(idx)?.includes(type) ?? false;

  for (let i = 1; i < chartData.length; i++) {
    const candle   = chartData[i];
    const patterns = patternByIndex.get(i);

    // ── Bullish Reversal Setup ─────────────────────────────────────────────
    if ((patterns === 'hammer' || patterns === 'bull_engulf') && hasBadge(i, 'rsi_os')) {
      results.push({
        index: i, type: 'bullish_reversal',
        label: 'Bull Rev', color: '#10b981', direction: 'bull',
        strength: 'HIGH',
        desc: 'Bullish Reversal Setup',
        conditions: [
          `${patterns === 'hammer' ? 'Hammer' : 'Bullish Engulfing'} candle`,
          'RSI crossed below 30 (oversold)',
        ],
      });
    }

    // ── Bearish Reversal Setup ─────────────────────────────────────────────
    if ((patterns === 'shooting_star' || patterns === 'bear_engulf') && hasBadge(i, 'rsi_ob')) {
      results.push({
        index: i, type: 'bearish_reversal',
        label: 'Bear Rev', color: '#ef4444', direction: 'bear',
        strength: 'HIGH',
        desc: 'Bearish Reversal Setup',
        conditions: [
          `${patterns === 'shooting_star' ? 'Shooting Star' : 'Bearish Engulfing'} candle`,
          'RSI crossed above 70 (overbought)',
        ],
      });
    }

    // ── Morning Star Confirmed ─────────────────────────────────────────────
    // Pattern fires on bar i; MACD flip on same or next bar
    const nextBadges = badgeByIndex.get(i + 1) || [];
    if (patterns === 'morning_star' && (hasBadge(i, 'macd_bull') || nextBadges.includes('macd_bull'))) {
      results.push({
        index: i, type: 'morning_star_confirmed',
        label: 'M⭐+M↑', color: '#10b981', direction: 'bull',
        strength: 'VERY HIGH',
        desc: 'Morning Star Confirmed',
        conditions: [
          '3-bar Morning Star pattern',
          'MACD histogram flipped positive (independent confirmation)',
        ],
      });
    }

    // ── Evening Star Confirmed ─────────────────────────────────────────────
    if (patterns === 'evening_star' && (hasBadge(i, 'macd_bear') || nextBadges.includes('macd_bear'))) {
      results.push({
        index: i, type: 'evening_star_confirmed',
        label: 'E⭐+M↓', color: '#ef4444', direction: 'bear',
        strength: 'VERY HIGH',
        desc: 'Evening Star Confirmed',
        conditions: [
          '3-bar Evening Star pattern',
          'MACD histogram flipped negative (independent confirmation)',
        ],
      });
    }

    // ── Volume Surge Bull ──────────────────────────────────────────────────
    if ((patterns === 'hammer' || patterns === 'bull_engulf') && hasBadge(i, 'rsi_os')) {
      const slice  = chartData.slice(Math.max(0, i - 20), i);
      const avgVol = slice.reduce((s, d) => s + d.volume, 0) / (slice.length || 1);
      if (candle.volume >= avgVol * 2) {
        results.push({
          index: i, type: 'volume_surge_bull',
          label: 'Vol↑Bull', color: '#10b981', direction: 'bull',
          strength: 'HIGH',
          desc: 'Volume Surge Bullish',
          conditions: [
            `${patterns === 'hammer' ? 'Hammer' : 'Bullish Engulfing'} candle`,
            'RSI oversold',
            `Volume ${(candle.volume / avgVol).toFixed(1)}× 20-bar average`,
          ],
        });
      }
    }
  }

  return results;
}
